import { useEffect, useRef } from 'react';

const YT_API_SRC = 'https://www.youtube.com/iframe_api';

type YTStateChangeEvent = { data: number };
type YTPlayer = { destroy?: () => void };
type YTApi = {
  Player: new (
    id: string,
    opts: { events: { onStateChange: (e: YTStateChangeEvent) => void } },
  ) => YTPlayer;
  PlayerState: { PLAYING: number };
};

declare global {
  interface Window {
    YT?: YTApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

function loadYouTubeApi(): Promise<YTApi> {
  if (typeof window === 'undefined') {
    return Promise.reject();
  }
  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }

  return new Promise<YTApi>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${YT_API_SRC}"]`);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (window.YT) {
        resolve(window.YT);
      } else {
        reject();
      }
    };
    if (!existing) {
      const tag = document.createElement('script');
      tag.src = YT_API_SRC;
      document.body.appendChild(tag);
    }
  });
}

type UseYouTubePlayerArgs = {
  iframeId: string;
  isOpen: boolean;
  onPlay?: () => void;
};

export function useYouTubePlayer({ iframeId, isOpen, onPlay }: UseYouTubePlayerArgs): void {
  const hasPlayedRef = useRef(false);
  const playerRef = useRef<YTPlayer | null>(null);

  useEffect(() => {
    if (!isOpen) {
      hasPlayedRef.current = false;
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch {}
        playerRef.current = null;
      }
      return;
    }
    if (!onPlay) {
      return;
    }

    let cancelled = false;
    const init = async () => {
      const YT = await loadYouTubeApi();
      if (cancelled) {
        return;
      }
      playerRef.current = new YT.Player(iframeId, {
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING && !hasPlayedRef.current) {
              hasPlayedRef.current = true;
              onPlay();
            }
          },
        },
      });
    };
    void init();

    return () => {
      cancelled = true;
    };
  }, [isOpen, onPlay, iframeId]);
}
